import { MapViewerConfiguration } from "../../config/map-viewer-configuration";
import { DataTransporter } from "../data-transporter";
import { RootStoreSession } from "../root-store";
import { CreationAreaDto, UsageAreaDto } from "./area-dto";
import { Area, CreationArea, UsageArea } from "./area";
import { DataNotFound } from "../../globals/errors";

export class AreaStore {
    mapViewerConfig: MapViewerConfiguration;
    rootStore: RootStoreSession;
    dataTransporter: DataTransporter;
    creationAreas: CreationArea[];
    usageAreas: UsageArea[];

    constructor(mapViewerConfig: MapViewerConfiguration, rootStore: RootStoreSession, dataTransporter: DataTransporter) {
        this.mapViewerConfig = mapViewerConfig;
        this.rootStore = rootStore;
        this.dataTransporter = dataTransporter;
        this.creationAreas = [];
        this.usageAreas = [];
    }

    async loadAll(): Promise<Area[]> {
        const creationAreas = await this.loadAllCreationAreas();
        const usageAreas = await this.loadAllUsageAreas();
        return ([] as Area[]).concat(creationAreas, usageAreas);
    }

    async loadAllCreationAreas(): Promise<CreationArea[]> {
        this.creationAreas = [];
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-creation-areas`);
        const areaDtos: CreationAreaDto[] = result.data;
        areaDtos.forEach(a => this.creationAreas.push(new CreationArea(a, this.rootStore)));
        return this.creationAreas;
    }

    async loadAllUsageAreas(): Promise<UsageArea[]> {
        this.usageAreas = [];
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-usage-areas`);
        const areaDtos: UsageAreaDto[] = result.data;
        areaDtos.forEach(a => this.usageAreas.push(new UsageArea(a, this.rootStore)));
        return this.usageAreas;
    }

    async loadCreationAreas(options: FindFilter): Promise<CreationArea[]> {
        this.creationAreas = [];
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-creation-areas?fffCreationProjectId=${options.projectId}`);
        const areaDtos: CreationAreaDto[] = result.data;
        areaDtos.forEach(a => this.creationAreas.push(new CreationArea(a, this.rootStore)));
        return this.creationAreas;
    }

    async loadUsageAreas(options: FindFilter): Promise<UsageArea[]> {
        this.usageAreas = [];
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-usage-areas?fffUsageProjectId=${options.projectId}`);
        const areaDtos: UsageAreaDto[] = result.data;
        areaDtos.forEach(a => this.usageAreas.push(new UsageArea(a, this.rootStore)));
        return this.usageAreas;
    }

    async loadCreationArea(id: number): Promise<CreationArea> {
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-creation-areas/${id}`);
        const areaDto: CreationAreaDto = result.data;
        const area = new CreationArea(areaDto, this.rootStore);
        this.creationAreas.push(area);
        return area;
    }

    async loadUsageArea(id: number): Promise<UsageArea> {
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-usage-areas/${id}`);
        const areaDto: UsageAreaDto = result.data;
        const area = new UsageArea(areaDto, this.rootStore);
        this.usageAreas.push(area);
        return area;
    }

    async updateCreationArea(areaDto: CreationAreaDto): Promise<CreationArea> {
        const area = this.creationAreas.find(a => a.id == areaDto.id);

        if (area !== undefined) {
            const result = await this.dataTransporter.put(`${this.mapViewerConfig.api_base_url}/fff-creation-areas/${areaDto.id}`, areaDto);
            area.areaDto = result.data;
            return area;
        }

        throw new DataNotFound("Creation Area not found");
    }
}

type FindFilter = {
    projectId: number;
};
